/**
 * BeaconSystem.ts — Future Beacon signal strength (§9.5).
 *
 * Each tick:
 *   signal += nearbyFriendlyUnits * SIGNAL_FRIENDLY_BONUS * delta
 *   signal -= nearbyEnemies       * SIGNAL_ENEMY_DRAIN    * delta
 *   signal -= jamming                                      * delta
 *   signal = clamp(signal, 0, BEACON_MAX_SIGNAL)
 *
 * Bands (applied to ResourceSystem when the band changes):
 *   > 75  prophecy  — song-drop timer ticks; prophecy warnings active
 *   50–75 normal    — standard income
 *   25–50 reduced   — reputation income × 0.75
 *   < 25  critical  — reputation income paused + sales drain 5/s
 *
 * Ownership:
 *   player-owned — normal Future Beacon signal rules
 *   enemy-owned  — red pulse hazard until good forces recapture the plaza
 *
 * Song Drop: signal must stay above SONG_DROP_THRESHOLD for
 * SONG_DROP_SUSTAIN_TIME seconds continuously.  Check songDropReady.
 */

import type { FutureBeacon }  from '../buildings/FutureBeacon';
import type { ResourceSystem } from './ResourceSystem';
import {
  SIGNAL_FRIENDLY_BONUS,
  SIGNAL_ENEMY_DRAIN,
  BEACON_MAX_SIGNAL,
  SONG_DROP_THRESHOLD,
  SONG_DROP_SUSTAIN_TIME,
  SIGNAL_BAND_PROPHECY,
  SIGNAL_BAND_NORMAL,
  SIGNAL_BAND_REDUCED,
  SIGNAL_SALES_DRAIN_CRITICAL,
  BEACON_CAPTURE_TIME,
} from '../constants';

export type SignalBand = 'prophecy' | 'normal' | 'reduced' | 'critical';
export type BeaconOwner = 'player' | 'enemy';

export interface BeaconSystemState {
  owner: BeaconOwner;
  captureTimer: number;
}

export class BeaconSystem {
  private sustainedAbove = 0; // seconds signal has held above song-drop threshold
  private _prevBand: SignalBand = 'normal';
  private owner: BeaconOwner = 'player';
  private captureTimer  = 0;  // seconds the opposing side has held the beacon uncontested
  private signalBoostTimer = 0; // Signal Boost spell — doubles positive flux while > 0

  constructor(
    private readonly beacon:    FutureBeacon,
    private readonly resources: ResourceSystem,
  ) {}

  /**
   * Run one signal frame.
   * @param delta           Real-time seconds (NOT tempo-scaled — signal is musical ambience)
   * @param nearbyFriendly  Player units within BEACON_NEAR_RADIUS of the beacon
   * @param nearbyEnemies   Enemy units within BEACON_NEAR_RADIUS of the beacon
   * @param jamming         Extra drain from Streaming Mogul abilities (Stage 11+)
   */
  update(
    delta:          number,
    nearbyFriendly: number,
    nearbyEnemies:  number,
    jamming:        number,
  ): void {
    // ── Signal Boost spell: doubles the friendly flux contribution ────────
    if (this.signalBoostTimer > 0) {
      this.signalBoostTimer = Math.max(0, this.signalBoostTimer - delta);
    }
    const boostMult = this.signalBoostTimer > 0 ? 2.0 : 1.0;

    // ── Signal formula ──────────────────────────────────────────────────
    // Enemy control corrupts the Beacon: no friendly signal accumulates until
    // the plaza is cleared and reclaimed.
    const friendlyFlux = this.owner === 'player'
      ? nearbyFriendly * SIGNAL_FRIENDLY_BONUS * boostMult
      : 0;
    const flux = (
      friendlyFlux
      - nearbyEnemies * SIGNAL_ENEMY_DRAIN
      - jamming
    ) * delta;

    this.beacon.signalStrength = Math.min(
      BEACON_MAX_SIGNAL,
      Math.max(0, this.beacon.signalStrength + flux),
    );

    // ── Song-drop sustain timer ─────────────────────────────────────────
    // Timer must be continuous — any dip below threshold resets the clock.
    if (this.owner === 'player' && this.beacon.signalStrength > SONG_DROP_THRESHOLD) {
      this.sustainedAbove += delta;
    } else {
      this.sustainedAbove = 0;
    }

    // ── Ownership capture / recapture ──────────────────────────────────
    this.updateOwnership(delta, nearbyFriendly, nearbyEnemies);

    // ── Band-change modifiers ───────────────────────────────────────────
    const band = this.signalBand;
    if (band !== this._prevBand) {
      const [repMult, salesDrain] = this.bandParams(band);
      this.resources.setSignalModifiers(repMult, salesDrain);
      this._prevBand = band;
    }
  }

  // ── Public getters ────────────────────────────────────────────────────

  /** Current signal value 0–100. */
  get signal(): number { return this.beacon.signalStrength; }

  /** Active signal band based on current signal strength. */
  get signalBand(): SignalBand {
    if (this.owner === 'enemy') return 'critical';
    const s = this.beacon.signalStrength;
    if (s > SIGNAL_BAND_PROPHECY) return 'prophecy';
    if (s > SIGNAL_BAND_NORMAL)   return 'normal';
    if (s > SIGNAL_BAND_REDUCED)  return 'reduced';
    return 'critical';
  }

  /**
   * True once signal has been continuously above the threshold for the
   * full sustain time — triggers a Song Drop event in AudioSystem (Stage 17).
   */
  get songDropReady(): boolean {
    return this.sustainedAbove >= SONG_DROP_SUSTAIN_TIME;
  }

  /**
   * Seconds into the current song-drop sustain window (0 if below threshold).
   * Useful for the HUD countdown.
   */
  get songDropProgress(): number { return this.sustainedAbove; }

  /** Whether the prophecy band is active — enables early wave warnings. */
  get prophecyActive(): boolean {
    return this.owner === 'player' && this.signalBand === 'prophecy';
  }

  /** 0..1 progress toward the current Beacon ownership flip. */
  get captureProgress(): number {
    return Math.min(this.captureTimer / BEACON_CAPTURE_TIME, 1);
  }

  /** Current Future Beacon owner. */
  get beaconOwner(): BeaconOwner { return this.owner; }

  /** True while enemies control the central Future Beacon. */
  get isEnemyControlled(): boolean { return this.owner === 'enemy'; }

  /** Back-compat: true while enemies control the central Future Beacon. */
  get isCaptured(): boolean {
    return this.owner === 'enemy';
  }

  /**
   * Consume the song drop — resets the sustain timer so the player must
   * hold signal above the threshold again to earn the next drop.
   */
  consumeSongDrop(): void {
    this.sustainedAbove = 0;
  }

  /** Signal Boost spell — double signal output for the given duration. */
  activateSignalBoost(duration: number): void {
    this.signalBoostTimer = Math.max(this.signalBoostTimer, duration);
  }

  /** True while Signal Boost is active. */
  get signalBoostActive(): boolean { return this.signalBoostTimer > 0; }

  getState(): BeaconSystemState {
    return {
      owner: this.owner,
      captureTimer: this.captureTimer,
    };
  }

  restoreState(state?: Partial<BeaconSystemState>): void {
    this.owner = state?.owner === 'enemy' ? 'enemy' : 'player';
    this.captureTimer = Math.max(0, state?.captureTimer ?? 0);
    if (this.owner === 'enemy') {
      this.beacon.signalStrength = 0;
      this.sustainedAbove = 0;
    }
    this._prevBand = this.signalBand;
    const [repMult, salesDrain] = this.bandParams(this._prevBand);
    this.resources.setSignalModifiers(repMult, salesDrain);
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  /**
   * Per-band resource modifier parameters.
   * Returns [repIncomeMultiplier, salesDrainPerSecond].
   */
  private bandParams(band: SignalBand): [number, number] {
    switch (band) {
      case 'prophecy': return [1.0, 0];
      case 'normal':   return [1.0, 0];
      case 'reduced':  return [0.75, 0];
      case 'critical': return [0, SIGNAL_SALES_DRAIN_CRITICAL];
    }
  }

  private updateOwnership(delta: number, nearbyFriendly: number, nearbyEnemies: number): void {
    if (this.owner === 'player') {
      if (nearbyEnemies > 0 && nearbyFriendly === 0 && this.beacon.signalStrength === 0) {
        this.captureTimer += delta;
      } else {
        this.captureTimer = 0;
      }
      if (this.captureTimer >= BEACON_CAPTURE_TIME) {
        this.owner = 'enemy';
        this.captureTimer = 0;
        this.beacon.signalStrength = 0;
        this.sustainedAbove = 0;
      }
      return;
    }

    if (nearbyFriendly > 0 && nearbyEnemies === 0) {
      this.captureTimer += delta;
    } else {
      this.captureTimer = 0;
    }
    if (this.captureTimer >= BEACON_CAPTURE_TIME) {
      this.owner = 'player';
      this.captureTimer = 0;
      this.beacon.signalStrength = Math.max(this.beacon.signalStrength, SIGNAL_BAND_NORMAL);
    }
  }
}
